import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

const AmanBenefitsModal = ({ show, onClose }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage !== "en";
  const [isMobile, setIsMobile] = useState(
      () => typeof window !== "undefined" && window.innerWidth < 768
    );

    // Frozen-at-open presentation mode so a viewport resize mid-transition can't
    // swap mobile bottom-sheet motion with desktop scale/fade. Computed
    // synchronously during render (ref-based) so the very first enter frame
    // already has the correct value.
    const presentationMobileRef = useRef(isMobile);
    const prevShowRef = useRef(show);
    if (show && !prevShowRef.current) {
      // Open edge: capture the current breakpoint and hold it for the whole
      // visible lifecycle. Resizes after this won't swap motion config or
      // layout mode until the modal fully closes and re-opens.
      presentationMobileRef.current = isMobile;
    }
    prevShowRef.current = show;
    const presentationMobile = presentationMobileRef.current;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Lock body scroll while modal is open. We deliberately do NOT unlock in
    // the [show] cleanup, because that runs the instant show flips to false —
    // before the exit animation finishes. Unlock happens in onExitComplete and
    // a separate unmount-only effect handles unmount-while-open.
    useEffect(() => {
      if (show) {
        document.body.style.overflow = "hidden";
      }
    }, [show]);

    useEffect(() => {
      return () => {
        document.body.style.overflow = "";
      };
    }, []);

    // Track latest show value via ref so onExitComplete (which can fire after
    // a rapid close→reopen) only releases the scroll lock if the modal is
    // actually closed at the moment the exit animation finishes.
    const latestShowRef = useRef(show);
    latestShowRef.current = show;

    const handleExitComplete = () => {
      if (!latestShowRef.current) {
        document.body.style.overflow = "";
      }
    };

  const amanBenefits = [
    {
      description: translate("Annual Maximum Limit", "الحد السنوي الأقصى"),
      value: translate("BHD 2,000 per person per year", "2,000 دينار بحريني للشخص في السنة"),
    },
    {
      description: translate("Territorial Limit", "الحدود الجغرافية"),
      value: translate("Bahrain", "البحرين"),
    },
    {
      description: translate("In-Patient & Day Care", "العلاج الداخلي والرعاية اليومية"),
      value: translate("Covered (subject to 3 months waiting period for surgical procedures)", "مغطى (تغطى العمليات الجراحية بعد ٣ شهور من تاريخ بدء الوثيقة)"),
    },
    {
      description: translate("Outpatient", "العلاج في العيادات الخارجية"),
      value: translate("BHD 500 per person per year", "500 دينار بحريني للشخص في السنة"),
    },
    {
      description: translate("Consultation", "الإستشارة"),
      value: translate("GP (Specialist/Consultant upon referral by GP)", "عند الطبيب العام (الإستشارة عند الأخصائي/الإستشاري مغطاة عندما تحول"),
    },
    {
      description: translate("Diagnostic Procedures (X-ray & Laboratory tests)", "الفحوصات التشخيصية ( الأشعة والمختبر )"),
      value: translate("Covered (Subject to Approval)", "مغطى (يتطلب موافقة مسبقة )"),
    },
    {
      description: translate("Pharmacy", "الصيدلية"),
      value: translate("Generic Medications only and following a prescription by the treating Doctor", "الأدوية المكافئة فقط بناءاً على وصفة طبية من الطبيب المعالج"),
    },
    {
      description: translate("Deductible Per Consultation", "مبلغ التحمل لكل استشارة"),
      value: "5",
    },
    {
      description: translate("Pre-Existing and chronic conditions", "الحالات السابقة والأمراض المزمنة"),
      value: translate("Not covered", "غير مغطى"),
    },
    {
      description: [
        translate("Dental: Limited to consultation, amalgam filling, simple extraction & medications", "علاج الأسنان: يغطي فقط الاستشارات، الحشوات الأملغمية، خلع الأسنان البسيط، والأدوية"),
        translate("Coinsurance applicable on Dental treatment", "المشاركة التأمينية"),
      ],
      value: [
        translate("BHD 60 per person per year", "60 دينار بحريني للشخص في السنة"),
        "20%",
      ],
    },
    {
      description: [
        translate("Maternity: limited to 9 prenatal visits and 1 postnatal visit", "الحمل: تسع زيارات متابعة أثناء فترة الحمل وزيارة واحدة بعد الولادة"),
        translate("Coinsurance applicable on maternity treatment", "المشاركة التأمينية"),
      ],
      value: [
        translate("BHD 100 per member per year", "100 دينار بحريني للشخص في السنة"),
        "20%",
      ],
    },
    {
      description: translate("Network", "مقدمي الخدمة المعتمدين"),
      value: translate("As per the provided list", "بحسب القائمة المعطاة"),
    },
  ];

  const deathBenefits = [
    {
      description: translate("Death due to accident", "الوفاة بسبب حادث"),
      amount: translate("BHD 2,500 per Domestic Helper (or the minimum requirement established by the Embassy of the Domestic Helper, subject to a maximum of BD 5,000 per Domestic Helper)", "مبلغ مقطوع 2,500 دينار بحريني لكل عامل (أو الحد الأدنى المطلوب من سفارة العامل, وتخضع الى حد أقصى قدره 5000 دينار بحريني )"),
      payableTo: translate("Legal heirs through the embassy", "الورثة الشرعيين للعامل من خلال السفارة"),
    },
    {
      description: translate("Death due to any other cause", "الوفاة الناجمة عن أي سبب آخر"),
      amount: translate("BHD 1,000", "1,000 دينار بحريني"),
      payableTo: translate("Legal heirs of the domestic helper", "الورثة الشرعيين للعامل من خلال السفارة"),
    },
    {
      description: translate("Body Repatriation costs following death by any cause", "مصاريف إعادة الجثة بعد الوفاة الناجمه عن أي سبب"),
      amount: translate("BHD 800", "800 دينار بحريني"),
      payableTo: translate("Sponsor", "الكفيل"),
    },
    {
      description: translate("Cost of replacing domestic helper following death by any cause", "تكلفة استبدال العامل بعد وفاة ناجمه عن أي سبب آخر"),
      amount: translate("BHD 500", "500 دينار بحريني"),
      payableTo: translate("Sponsor", "الكفيل"),
    },
  ];

  const personalAccidentBenefits = [
    { description: translate("Permanent loss of both hands or feet", "فقدان دائم لليدين أو القدمين"), amount: "100%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Total & irremediable blindness in both eyes", "فقدان كامل للبصر لا علاج له في العينين"), amount: "100%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Total & irremediable blindness in one eye & loss of one hand or one foot", "فقدان كامل للبصر لا علاج له في العين الواحدة و فقدان يد واحدة, او قدم واحدة"), amount: "100%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Permanent loss of one hand or one foot", "فقدان دائم ليد واحدة أو قدم واحدة"), amount: "50%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Total & irremediable blindness to one eye", "فقدان كامل للبصر لا علاج له في كلتا العينين"), amount: "50%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Loss of hearing in both ears", "فقدان السمع من الأذنين"), amount: "40%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Loss of hearing in one ear", "فقدان السمع من أذن واحدة"), amount: "10%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Right hand thumb", "فقدان إبهام اليد اليمنى"), amount: "20%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Left hand thumb", "فقدان إبهام اليد اليسرى"), amount: "10%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Right hand middle finger", "فقدان إصبع الاوسط لليد اليمنى"), amount: "15%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Left hand middle finger", "فقدان إصبع الاوسط لليد اليسرى"), amount: "10%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Right hand ring finger", "فقدان الخنصر لليد اليمنى"), amount: "6%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Left hand ring finger", "فقدان الخنصر لليد اليسرى"), amount: "5%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Little finger of both hands", "فقدان صبعا البنصر لليدين"), amount: "5%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    { description: translate("Any toe in feet", "فقدان أي إصبع في القدم"), amount: "3%", payableTo: translate("Domestic helper", "العامل المنزلي") },
    {
      description: translate("A weekly benefit in case of Temporary Total Disablement", "مبالغ إسبوعية في حالة الإعاقة الكلية المؤقتة"),
      amount: translate("BHD 15 per week", "15 دينار بحريني أسبوعياً"),
      amountNote: translate("during the disablement period but not exceeding 104 weeks per Domestic Helper", "خلال فترة الأعاقه على ألا تطول المدة لأكثر من 104 أسابيع لكل عامل"),
      payableTo: translate("Sponsor", "الكفيل"),
    },
  ];

  const panelInitial = presentationMobile
      ? { y: "100%", opacity: 1 }
      : { opacity: 0, scale: 0.96, y: 8 };
    const panelAnimate = presentationMobile
      ? { y: 0, opacity: 1 }
      : { opacity: 1, scale: 1, y: 0 };
    const panelExit = presentationMobile
      ? { y: "100%", opacity: 1 }
      : { opacity: 0, scale: 0.96, y: 8 };
    const panelTransition = presentationMobile
      ? { type: "spring", stiffness: 340, damping: 34, mass: 0.7 }
      : { type: "spring", stiffness: 380, damping: 32, mass: 0.6 };

    return (
      <AnimatePresence onExitComplete={handleExitComplete}>
        {show && (
          <div className="fixed inset-0 z-[99999]">
            <motion.div
              className="fixed inset-0 bg-black/60"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            />

            <motion.div
              initial={panelInitial}
              animate={panelAnimate}
              exit={panelExit}
              transition={panelTransition}
              style={{ willChange: "transform, opacity" }}
              className={`
          fixed bg-white flex flex-col z-10 overflow-hidden
          ${presentationMobile 
            ? 'inset-x-0 bottom-0 top-[10vh] rounded-t-[26px]' 
            : 'left-0 right-0 mx-auto top-[30px] bottom-[30px] w-[95vw] max-w-[1248px] rounded-[26px]'
          }
        `}>
        {presentationMobile && (
          <div className="flex justify-center pt-3 pb-2 flex-shrink-0 rounded-t-[26px]">
            <div className="w-10 h-1 bg-[#DDE2E4] rounded-full" />
          </div>
        )}

        <div 
          dir={isRTL ? "rtl" : "ltr"}
          className={`flex items-center justify-between bg-white flex-shrink-0 ${presentationMobile ? 'px-6 pb-6 pt-0 rounded-t-[26px]' : 'p-6 pb-4'}`}
        >
          <h2 className={`text-[#1A2024] font-semibold leading-8 tracking-[-0.24px] ${presentationMobile ? 'text-[24px] w-[305px]' : 'text-2xl'}`}>
            {translate("Aman and Aman Plus benefits & covers", "فوائد أمان")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>

        <div 
          dir={isRTL ? "rtl" : "ltr"}
          className={`flex-1 overflow-y-auto flex flex-col gap-12 ${presentationMobile ? 'px-6 pb-6' : 'px-6 pb-6'}`}
        >
          <div className="flex flex-col gap-6 w-full">
            <div className="bg-white border border-[#DDE2E4] rounded-[10px] overflow-hidden w-full">
              {/* Table header - same for both mobile and desktop */}
              <div 
                className={`bg-[#F6F8F9] border-b border-[#DDE2E4] flex items-center ${presentationMobile ? 'px-2 py-6' : 'py-6'}`}
              >
                <div className={`${presentationMobile ? 'w-1/2' : 'w-[500px]'} ${presentationMobile ? '' : 'px-6'}`}>
                  <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                    {translate("Description", "الوصف")}
                  </p>
                </div>
                <div className={`${presentationMobile ? 'w-1/2 px-4' : 'flex-1 px-6'}`}>
                  <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                    {translate("Benefits & Covers", "الفوائد والتغطيات")}
                  </p>
                </div>
              </div>

              {amanBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-start border-b border-[#DDE2E4] last:border-b-0 ${presentationMobile ? 'px-2 py-6' : 'py-6'}`}
                >
                  <div className={`${presentationMobile ? 'w-1/2' : 'w-[500px] px-6'}`}>
                    {Array.isArray(benefit.description) ? (
                      benefit.description.map((desc, i) => (
                        <p key={i} className={`text-[#252C32] font-semibold leading-6 mb-2 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                          {desc}
                        </p>
                      ))
                    ) : (
                      <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                        {benefit.description}
                      </p>
                    )}
                  </div>
                  <div className={`${presentationMobile ? 'w-1/2 px-4' : 'flex-1 px-6'}`}>
                    {Array.isArray(benefit.value) ? (
                      benefit.value.map((val, i) => (
                        <p key={i} className={`text-[#252C32] font-normal leading-6 mb-2 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                          {val}
                        </p>
                      ))
                    ) : (
                      <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                        {benefit.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className={`text-[#1A2024] font-semibold leading-8 tracking-[-0.24px] ${presentationMobile ? 'text-[18px]' : 'text-2xl'}`}>
              {translate("Aman Plus benefits & covers", " Plus فوائد أمَانْ")}
            </h3>

            <div className="bg-white border border-[#DDE2E4] rounded-[10px] overflow-hidden w-full">
              {/* Table header with 3 columns matching the row layout below:
                  description | amount | payable to */}
              <div
                className={`bg-[#F6F8F9] border-b border-[#DDE2E4] flex items-start ${presentationMobile ? 'px-2 py-6' : 'py-6'}`}
              >
                <div className={`${presentationMobile ? 'w-[35%]' : 'w-[500px] px-4'}`}>
                  <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                    {translate("Death of Domestic Helper", "في حالة وفاة العامل المنزلي")}
                  </p>
                </div>
                <div className={`${presentationMobile ? 'w-[40%] px-2' : 'w-[350px] px-6'}`}>
                  <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                    {translate("Amount", "المبلغ")}
                  </p>
                </div>
                <div className={`${presentationMobile ? 'w-[25%] px-2' : 'flex-1 px-6'}`}>
                  <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                    {translate("Payable to", "المستحق")}
                  </p>
                </div>
              </div>

              {deathBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-start border-b border-[#DDE2E4] ${presentationMobile ? 'px-2 py-6' : 'py-6'}`}
                >
                  <div className={`${presentationMobile ? 'w-[35%]' : 'w-[500px] px-4'}`}>
                    <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                      {benefit.description}
                    </p>
                  </div>
                  <div className={`${presentationMobile ? 'w-[40%] px-2' : 'w-[350px] px-6'}`}>
                    <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                      {benefit.amount}
                    </p>
                  </div>
                  <div className={`${presentationMobile ? 'w-[25%] px-2' : 'flex-1 px-6'}`}>
                    <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                      {benefit.payableTo}
                    </p>
                  </div>
                </div>
              ))}

              {/* Personal accident note row */}
              <div className={`border-b border-[#DDE2E4] ${presentationMobile ? 'px-2 py-6' : 'py-6 px-4'}`}>
                <p className={`text-[#005574] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                  {translate(
                    "Personal Accidents A percentage of the limit of indemnity in case of Permanent Disablement",
                    "النسبة المئوية للتعويض للحوادث الشخصية في حالة العجز الدائم كنسبة مئوية من مبلغ التعويض الكلي"
                  )}
                </p>
              </div>

              {personalAccidentBenefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`flex items-start border-b border-[#DDE2E4] last:border-b-0 ${presentationMobile ? 'px-2 py-6' : 'py-6'}`}
                >
                  <div className={`${presentationMobile ? 'w-[35%]' : 'w-[500px] px-4'}`}>
                    <p className={`text-[#252C32] font-semibold leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                      {benefit.description}
                    </p>
                  </div>
                  <div className={`${presentationMobile ? 'w-[40%] px-2' : 'w-[350px] px-6'}`}>
                    {benefit.amountNote ? (
                      <div>
                        <p className={`text-[#252C32] font-semibold leading-6 mb-3 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[18px] tracking-[-0.18px]'}`}>
                          {benefit.amount}
                        </p>
                        <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                          {benefit.amountNote}
                        </p>
                      </div>
                    ) : (
                      <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                        {benefit.amount}
                      </p>
                    )}
                  </div>
                  <div className={`${presentationMobile ? 'w-[25%] px-2' : 'flex-1 px-6'}`}>
                    <p className={`text-[#252C32] font-normal leading-6 ${presentationMobile ? 'text-[14px] tracking-[-0.14px]' : 'text-[16px] tracking-[-0.16px]'}`}>
                      {benefit.payableTo}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AmanBenefitsModal;
